/**
 * Finish Availability Handler
 *
 * Hides the finishes a wood variant cannot be made in, in OPC's (SE Option
 * Price Calculator) Finish dropdown and the Color, Stain and Primer choices
 * beneath it.
 *
 * Availability is authored per variant on the custom.finish_availability
 * metaobject — 35 booleans — and emitted by snippets/finish-availability-data
 * .liquid as the list of keys that are true. Product gating happens in Liquid:
 * wood products (tagged cabinet doors or drawer fronts, not rtf) with the
 * finish_availability_enabled theme setting on.
 *
 * It owns: the key-to-label table, the verdict for a variant, and every DOM
 * write to the four finish-options holders. It does NOT own the Backside
 * Finish dropdown (backside-finish-handler.js) or Unfinished, which is never
 * filtered. Custom Color is offered only where a paint color is.
 *
 * It fails OPEN. A variant with no metaobject filters nothing, and any label
 * it cannot match stays visible. "Unauthored" and "nothing available" are
 * different states kept apart all the way through: resolveKeys returns null
 * for the first and [] for the second.
 */

(function () {
  'use strict';

  /**
   * Metaobject key → the label OPC renders for it, exactly.
   *
   * This is the coupling point with OPC, whose options are authored in its own
   * admin. The labels do not follow the keys mechanically (Grey vs gray, Pool
   * House vs poolhouse, a " Stain" suffix, no "SW" prefix), so they are listed
   * rather than derived. If OPC renames an option, change it here and nowhere
   * else; the tests pin this table to the snippet's key list and to labels
   * captured from the live option sets.
   *
   * Color and Stain labels are the swatch input's data-value, Primer labels are
   * the dropdown entry's title, and Clear is the top-level entry (price
   * stripped).
   */
  var LABELS = {
    clear_east_clear: 'Clear Coat',

    color_accessible_beige_sw_7036: 'Accessible Beige 7036',
    color_alabaster_sw_7008: 'Alabaster 7008',
    color_anonymous_sw_7046: 'Anonymous 7046',
    color_antique_white_sw_6119: 'Antique White 6119',
    color_creamy_sw_7012: 'Creamy 7012',
    color_cyberspace_sw_7076: 'Cyberspace 7076',
    color_dorian_gray_sw_7017: 'Dorian Gray 7017',
    color_eventide_sw_9643: 'Eventide 9643',
    color_extra_white_sw_7006: 'Extra White 7006',
    color_gale_force_sw_7605: 'Gale Force 7605',
    color_mindful_gray_sw_7016: 'Mindful Gray 7016',
    color_naval_sw_6244: 'Naval 6244',
    color_pewter_green_sw_6208: 'Pewter Green 6208',
    color_poolhouse_sw_7603: 'Pool House 7603',
    color_pure_white_sw_7005: 'Pure White 7005',
    color_repose_gray_sw_7015: 'Repose Grey 7015',
    color_retreat_sw_6207: 'Retreat 6207',
    color_silver_strand_sw_7057: 'Silver Strand 7057',
    color_smoky_blue_sw_7604: 'Smoky Blue 7604',
    color_software_sw_7074: 'Software 7074',
    color_tricorn_black_sw_6258: 'Tricorn Black 6258',

    primer_east_primer_black_1_coat: 'Black',
    primer_east_primer_gray_1_coat: 'Grey',
    primer_east_primer_white_1_coat: 'White',

    stain_east_amber: 'Amber Stain',
    stain_east_coal: 'Coal Stain',
    stain_east_copper: 'Copper Stain',
    stain_east_limestone: 'Limestone Stain',
    stain_east_onyx: 'Onyx Stain',
    stain_east_pumice: 'Pumice Stain',
    stain_east_ruby: 'Ruby Stain',
    stain_east_smokey_quartz: 'Smokey Quartz Stain',
    stain_east_tigers_eye: 'Tigers Eye Stain',
    stain_east_topaz: 'Topaz Stain'
  };

  /**
   * The top-level Finish entry for each Finish Category, price stripped.
   * Unfinished is deliberately absent: it is never filtered, and an entry
   * that maps to no category is left alone.
   */
  var CATEGORY_LABELS = {
    clear: 'Clear Coat',
    color: 'Color',
    stain: 'Stain',
    primer: 'Prime Only'
  };

  function has(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  /** The Finish Category a key belongs to, from the metaobject's key prefix. */
  function categoryOfKey(key) {
    var prefix = String(key).split('_')[0];
    return has(CATEGORY_LABELS, prefix) ? prefix : null;
  }

  /**
   * Custom Color is a top-level entry but not a Finish Category: no key of its
   * own. Custom colors are paint, so it is offered exactly where at least one
   * standard paint color is.
   *
   * The label is typed in OPC's admin, so the British spelling is accepted too
   * — as custom-color-constants.js does for the same option. Both captured
   * option sets say "Custom Color" today.
   */
  var CUSTOM_COLOR_NAMES = ['custom color', 'custom colour'];

  // LABELS never changes, so group its keys once rather than on every verdict.
  // Clear is absent: it is a single top-level entry with no Finish Options.
  var KEYS_BY_CATEGORY = { color: [], stain: [], primer: [] };
  Object.keys(LABELS).forEach(function (key) {
    var category = categoryOfKey(key);
    if (has(KEYS_BY_CATEGORY, category)) KEYS_BY_CATEGORY[category].push(key);
  });

  // Every label the table knows, per category: which OPC labels are governed
  // at all (FA03) and which ones OPC failed to render (FA08).
  var LABELS_BY_CATEGORY = {};
  Object.keys(KEYS_BY_CATEGORY).forEach(function (category) {
    LABELS_BY_CATEGORY[category] = KEYS_BY_CATEGORY[category].map(function (key) {
      return LABELS[key];
    });
  });

  function isStringList(value) {
    if (!Array.isArray(value)) return false;
    for (var i = 0; i < value.length; i++) {
      if (typeof value[i] !== 'string') return false;
    }
    return true;
  }

  /**
   * This variant's available keys, or null when the merchant has not authored
   * it (or the payload is not the shape we emit).
   *
   * Null is not an empty list: [] means no finish in scope can be made and the
   * categories should come off the dropdown, null means we know nothing and
   * must leave the dropdown alone.
   *
   * The product payload holds the list directly; a cart payload entry holds it
   * under `keys`, beside the variant's options.
   */
  function resolveKeys(data, variantId) {
    if (!data || !data.variants || variantId === null || variantId === undefined) return null;
    var value = data.variants[String(variantId)];
    if (value && !Array.isArray(value) && typeof value === 'object') value = value.keys;
    return isStringList(value) ? value : null;
  }

  /** Keys in a payload that the label table does not know (FA02). */
  function unknownKeys(keys) {
    return (keys || []).filter(function (key) {
      return !has(LABELS, key);
    });
  }

  /**
   * The verdict for a variant: which top-level entries stay visible and which
   * Finish Options each category keeps.
   *
   * A category is visible when at least one of its keys is available, so the
   * customer is never offered a category that opens onto nothing. Keys the
   * label table does not know are ignored here and reported by unknownKeys.
   *
   * Null keys (an unauthored variant) get a null verdict, not the all-hidden
   * one [] produces — so plan(resolveKeys(...)) fails open by construction,
   * and needsReset treats a null verdict as nothing to reset.
   */
  function plan(keys) {
    if (keys === null || keys === undefined) return null;
    var available = {};
    keys.forEach(function (key) {
      if (has(LABELS, key)) available[key] = true;
    });

    var categories = {};
    Object.keys(KEYS_BY_CATEGORY).forEach(function (category) {
      var allowed = KEYS_BY_CATEGORY[category]
        .filter(function (key) {
          return available[key];
        })
        .map(function (key) {
          return LABELS[key];
        });
      categories[category] = { visible: allowed.length > 0, allowed: allowed };
    });

    return {
      clear: !!available.clear_east_clear,
      customColor: categories.color.visible,
      categories: categories
    };
  }

  /**
   * Compare labels on their words, not their spacing or case.
   *
   * OPC renders labels with trailing whitespace, and its admin accepts double
   * spaces and nbsp inside one; \s covers U+00A0, so collapsing runs handles
   * all of them.
   */
  function normalize(text) {
    return String(text === null || text === undefined ? '' : text)
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * What a top-level Finish entry stands for: a Finish Category,
   * 'customColor', or null for an entry this handler leaves alone.
   *
   * Titles carry their price ("Color + $47 Per Door Sqft"), so only the text
   * before the first " + " is compared — and compared exactly, so "Custom
   * Color + $500 [Add Note]" never reads as Color.
   */
  function topLevelEntry(title) {
    var name = normalize(String(title === null || title === undefined ? '' : title).split(' + ')[0]);
    if (!name) return null;
    if (CUSTOM_COLOR_NAMES.indexOf(name) !== -1) return 'customColor';
    for (var category in CATEGORY_LABELS) {
      if (has(CATEGORY_LABELS, category) && normalize(CATEGORY_LABELS[category]) === name) {
        return category;
      }
    }
    return null;
  }

  /**
   * True when an entry is OPC's placeholder rather than a real finish. Its
   * wording is "-- Select --" on some dropdowns and missing on others, so both
   * shapes count.
   */
  function isPlaceholderLabel(text) {
    var t = normalize(text);
    if (!t) return true;
    return t.replace(/[^a-z]/g, '') === 'select';
  }

  function isAllowed(label, allowed) {
    var t = normalize(label);
    return (allowed || []).some(function (candidate) {
      return normalize(candidate) === t;
    });
  }

  /**
   * True when what the customer has picked is no longer on offer.
   *
   * `selection` is { finish, option }: the top-level entry's title and the
   * Finish Option picked beneath it (blank when nothing is). A pick outside
   * the governed entries — Unfinished, the placeholder — is never stale.
   */
  function needsReset(selection, verdict) {
    if (!selection) return false;
    var shown = topLevelVisible(selection.finish, verdict);
    if (shown === null) return false;
    if (!shown) return true;

    var entry = verdict.categories[topLevelEntry(selection.finish)];
    if (!entry || isPlaceholderLabel(selection.option)) return false;
    return !isAllowed(selection.option, entry.allowed);
  }

  /**
   * Whether a top-level Finish entry should show, or null when the entry is
   * not this handler's to decide — Unfinished, the placeholder, or a finish
   * type OPC renders that the table does not know. Null is never touched.
   */
  function topLevelVisible(title, verdict) {
    var kind = topLevelEntry(title);
    if (!kind || !verdict) return null;
    if (kind === 'clear') return verdict.clear;
    if (kind === 'customColor') return verdict.customColor;
    return verdict.categories[kind].visible;
  }

  // Pressing the placeholder makes OPC re-render, which re-enters the guard;
  // that settles, because a cleared Finish is no longer stale. The cap is for
  // the case that does not settle — OPC restoring the stale pick every time —
  // which would otherwise be a hot loop re-pricing the configurator.
  var MAX_RESET_ATTEMPTS = 3;

  /**
   * A reset budget per variant change, around the DOM calls that read and
   * clear OPC's pick. One per context (product page, cart modal), so a
   * product page open behind the cart drawer does not spend the modal's
   * attempts.
   *
   * `opc` is { read, reset, onExhausted }: read() returns { finish, option },
   * reset() clears the Finish, onExhausted() reports FA05.
   */
  function makeResetGuard(opc) {
    var attempts = 0;
    var reported = false;
    return {
      reset: function () {
        attempts = 0;
        reported = false;
      },
      applyIfStale: function (verdict) {
        if (!needsReset(opc.read(), verdict)) return;
        if (attempts >= MAX_RESET_ATTEMPTS) {
          if (!reported) opc.onExhausted();
          reported = true;
          return;
        }
        attempts++;
        opc.reset();
      }
    };
  }

  /**
   * Whether one rendered Finish Option should show, or null when the label is
   * not in the table (the placeholder, or an option OPC's admin added or
   * renamed). Unknown labels fail open: they stay visible, and the caller
   * reports them (FA03).
   */
  function optionVisible(category, label, verdict) {
    if (!verdict || !has(LABELS_BY_CATEGORY, category)) return null;
    if (!isAllowed(label, LABELS_BY_CATEGORY[category])) return null;
    return isAllowed(label, verdict.categories[category].allowed);
  }

  /**
   * Table labels for a category that OPC did not render (FA08) — most likely a
   * rename in OPC's admin, which would otherwise leave that option unfiltered
   * with no signal at all.
   */
  function missingLabels(category, renderedLabels) {
    return LABELS_BY_CATEGORY[category].filter(function (label) {
      return !isAllowed(label, renderedLabels);
    });
  }

  /**
   * Which variant the cart "Edit Options" modal currently describes.
   *
   * The modal is OPC's own and carries no variant id — only native selects
   * named after each Shopify option ("Wood Type"). Recover the id by matching
   * those against each candidate's options, in the product's declared option
   * order. An option the modal does not expose cannot rule a candidate out —
   * but a selection that shares NO option with the product (the selects not
   * mounted yet, or named differently) recovers nothing, rather than letting
   * the first variant win by default.
   *
   * Same technique as backside-finish-handler.js and
   * variant-dimensions-handler.js, for the same modal. Copied rather than
   * shared, so three features are not coupled through a file none owns.
   */
  function variantFromModal(entry, selected) {
    if (!entry || !entry.optionNames || !entry.variants || !selected) return null;

    var comparable = entry.optionNames.some(function (optionName) {
      return has(selected, optionName);
    });
    if (!comparable) return null;

    for (var variantId in entry.variants) {
      if (!has(entry.variants, variantId)) continue;
      var variant = entry.variants[variantId];
      var options = variant && variant.options;
      if (!options) continue;

      var matches = true;
      for (var i = 0; i < entry.optionNames.length; i++) {
        var optionName = entry.optionNames[i];
        if (!has(selected, optionName)) continue;
        if (selected[optionName] !== options[i]) {
          matches = false;
          break;
        }
      }
      if (matches) return variantId;
    }

    return null;
  }

  /**
   * Which variant the open modal describes, as { id, fromLine }.
   *
   * Before the modal's option selects mount they share no option with the
   * product, and the modal still shows the line item it was opened for:
   * { id: lineVariantId, fromLine: true }. Once they have mounted, they are
   * the truth, and a combination matching no payload variant is an
   * unauthored one — unauthored variants are left out of the payload — so it
   * gets { id: null }, which filters nothing. Falling back to the line's
   * variant there would apply the wrong variant's filter.
   */
  function modalVariant(entry, selected, lineVariantId) {
    var comparable = !!(entry && entry.optionNames && selected) && entry.optionNames.some(function (optionName) {
      return has(selected, optionName);
    });
    if (!comparable) return { id: lineVariantId, fromLine: true };
    return { id: variantFromModal(entry, selected), fromLine: false };
  }

  /**
   * One lookup from every cart payload on the page: the cart page and the
   * cart drawer each render one. A payload that failed to parse arrives as
   * null and is skipped, so one bad block cannot take the others down.
   */
  function mergeCartPayloads(parsedList) {
    var merged = {};
    (parsedList || []).forEach(function (parsed) {
      if (!parsed || typeof parsed !== 'object') return;
      for (var productId in parsed) {
        if (has(parsed, productId)) merged[productId] = parsed[productId];
      }
    });
    return merged;
  }

  // Under node (tests), export the pure logic and skip all DOM wiring.
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      LABELS: LABELS,
      CATEGORY_LABELS: CATEGORY_LABELS,
      categoryOfKey: categoryOfKey,
      resolveKeys: resolveKeys,
      unknownKeys: unknownKeys,
      plan: plan,
      topLevelEntry: topLevelEntry,
      needsReset: needsReset,
      isPlaceholderLabel: isPlaceholderLabel,
      topLevelVisible: topLevelVisible,
      optionVisible: optionVisible,
      missingLabels: missingLabels,
      makeResetGuard: makeResetGuard,
      mergeCartPayloads: mergeCartPayloads,
      modalVariant: modalVariant,
      variantFromModal: variantFromModal
    };
    return;
  }

  // ---- DOM wiring (browser only) ----

  // main-product.liquid and the cart snippets can each load this file on one
  // page, and every <script> element runs; only the first copy wires anything.
  if (window.__finishAvailabilityLoaded) return;
  window.__finishAvailabilityLoaded = true;

  var STEP = '.stepHolder';
  var TOP_HOLDER = '.finish-options';
  var OPTION_HOLDERS = {
    color: '.finish-options__color',
    stain: '.finish-options__stain',
    primer: '.finish-options__primer'
  };
  var ENTRY_SELECTOR = '.option-type-dropdown-content > .tab-view';
  var SWATCH_WRAPPER = '.swatch-title-holder';
  var SWATCH_INPUT = 'input.swatch-input';
  // OPC keeps a dropdown's chosen value on this input, which survives its menu
  // re-renders; the visible label does not. The Finish dropdown's is the only
  // one inside TOP_HOLDER: the category holders are its siblings.
  var DROPDOWN_VALUE = 'input.dropbtn';
  // OPC's cart "Edit Options" modal. It renders the same step and holders as
  // the product page, so everything below works on a step root and both
  // contexts pass their own.
  var MODAL_ROOT = '.edit_cart_item';
  var MODAL_SELECT_PREFIX = 'myDropdown-';
  var CART_DATA = '[data-cart-finish-availability]';
  var LINE_ITEM = '.cart-item[data-fa-product]';
  // Marks what this handler hid, so clearing restores only its own writes and
  // never un-hides something OPC hid itself.
  var HIDDEN_ATTR = 'data-fa-hidden';
  var REAPPLY_DEBOUNCE_MS = 50;
  var BOOT_TIMEOUT_MS = 15000;
  var LOG_PREFIX = '[FinishAvailability]';

  var productScript = document.querySelector('[id^="FinishAvailability-"]');
  if (!productScript && !document.querySelector(CART_DATA)) return;

  var warned = {};
  /** Log once per code and subject. `level` is 'warn' unless given ('info'). */
  function warnOnce(code, subject, message, level) {
    var id = code + ':' + subject;
    if (warned[id]) return;
    warned[id] = true;
    console[level || 'warn'](LOG_PREFIX + ' ' + code + ' ' + message);
  }

  // The modal re-reads the cart payloads on every mutation; parse each script
  // once per text, so a drawer re-render that swaps the text still re-parses.
  var parsedPayloads = typeof WeakMap !== 'undefined' ? new WeakMap() : null;

  /** A payload script's JSON, or null (FA01: that payload filters nothing). */
  function parsePayload(el) {
    var text = el.textContent;
    var cached = parsedPayloads && parsedPayloads.get(el);
    if (cached && cached.text === text) return cached.value;
    var value = null;
    try {
      value = JSON.parse(text);
    } catch (e) {
      console.error(LOG_PREFIX + ' FA01 payload unparseable, filtering nothing:', e);
    }
    if (parsedPayloads) parsedPayloads.set(el, { text: text, value: value });
    return value;
  }

  function debounce(fn, wait) {
    var timer = null;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  }

  function setVisible(el, visible) {
    if (visible) {
      if (el.hasAttribute(HIDDEN_ATTR)) {
        el.style.display = '';
        el.removeAttribute(HIDDEN_ATTR);
      }
    } else if (!el.hasAttribute(HIDDEN_ATTR)) {
      el.setAttribute(HIDDEN_ATTR, '');
      el.style.display = 'none';
    }
  }

  function isPlaceholderEntry(entry) {
    return /-DEFAULT$/i.test(entry.getAttribute('id') || '') || isPlaceholderLabel(entry.getAttribute('title'));
  }

  function stepOf(holder) {
    return holder ? holder.closest(STEP) || holder.parentElement : null;
  }

  function holderFor(step, category) {
    return has(OPTION_HOLDERS, category) ? step.querySelector(OPTION_HOLDERS[category]) : null;
  }

  /**
   * The rendered Finish Options of one category, as { el, label }: el is what
   * gets hidden.
   * Color and Stain are swatches: the label is the radio's data-value and the
   * element hidden is the swatch's whole wrapper, so the dot and its caption go
   * together. Primer is a dropdown: the entry itself, by title.
   */
  function renderedOptions(step, category) {
    var holder = holderFor(step, category);
    if (!holder) return [];
    var out = [];
    if (category === 'primer') {
      var entries = holder.querySelectorAll(ENTRY_SELECTOR);
      for (var i = 0; i < entries.length; i++) {
        if (isPlaceholderEntry(entries[i])) continue;
        out.push({ el: entries[i], label: entries[i].getAttribute('title') });
      }
    } else {
      var wrappers = holder.querySelectorAll(SWATCH_WRAPPER);
      for (var j = 0; j < wrappers.length; j++) {
        var input = wrappers[j].querySelector(SWATCH_INPUT);
        if (input) out.push({ el: wrappers[j], label: input.getAttribute('data-value') });
      }
    }
    return out;
  }

  /** Put one step's Finish dropdown into the state `verdict` describes. */
  function applyVerdict(step, verdict) {
    var top = step.querySelector(TOP_HOLDER);
    if (!top) return;

    var entries = top.querySelectorAll(ENTRY_SELECTOR);
    for (var i = 0; i < entries.length; i++) {
      var visible = topLevelVisible(entries[i].getAttribute('title'), verdict);
      if (visible !== null) setVisible(entries[i], visible);
    }

    Object.keys(OPTION_HOLDERS).forEach(function (category) {
      var options = renderedOptions(step, category);
      var labels = [];
      options.forEach(function (option) {
        labels.push(option.label);
        var shown = optionVisible(category, option.label, verdict);
        if (shown === null) {
          warnOnce('FA03', category + ':' + option.label, 'OPC ' + category + ' option "' + option.label + '" is not in the label table; left visible.');
          return;
        }
        setVisible(option.el, shown);
      });
      // A category OPC rendered nothing for is the worst case of a rename, so it
      // warns too — but only while the verdict offers that category, when the
      // customer would open it onto nothing.
      if (!options.length && !verdict.categories[category].visible) return;
      missingLabels(category, labels).forEach(function (label) {
        warnOnce('FA08', category + ':' + label, 'label-table ' + category + ' option "' + label + '" was not rendered by OPC (renamed in its admin?); it cannot be filtered.');
      });
    });
  }

  /**
   * Undo everything this handler hid in one step. An unauthored variant shows
   * whatever OPC shows, not whatever the previous variant left behind.
   */
  function clearFilter(step) {
    var hidden = step.querySelectorAll('[' + HIDDEN_ATTR + ']');
    for (var i = 0; i < hidden.length; i++) setVisible(hidden[i], true);
  }

  /**
   * What the customer has picked in one step: the Finish entry's title and,
   * beneath it, the checked swatch (Color, Stain) or the Primer dropdown's
   * value.
   */
  function currentSelection(step) {
    var top = step.querySelector(TOP_HOLDER);
    var finishInput = top && top.querySelector(DROPDOWN_VALUE);
    var finish = finishInput ? finishInput.value : '';
    var kind = topLevelEntry(finish);
    var holder = holderFor(step, kind);
    var option = '';
    if (holder && kind === 'primer') {
      var primerInput = holder.querySelector(DROPDOWN_VALUE);
      option = primerInput ? primerInput.value : '';
    } else if (holder) {
      var checked = holder.querySelector(SWATCH_INPUT + ':checked');
      option = checked ? checked.getAttribute('data-value') : '';
    }
    return { finish: finish, option: option };
  }

  function placeholderIn(holder) {
    var entries = holder ? holder.querySelectorAll(ENTRY_SELECTOR) : [];
    for (var i = 0; i < entries.length; i++) {
      if (isPlaceholderEntry(entries[i])) return entries[i];
    }
    return null;
  }

  function fireValueEvents(input) {
    ['change', 'input'].forEach(function (type) {
      input.dispatchEvent(new Event(type, { bubbles: true }));
    });
  }

  /**
   * Press an OPC dropdown row the way a customer does, then tell OPC its value
   * changed so it re-prices.
   *
   * mousedown/mouseup and then exactly ONE click — never both .click() and a
   * dispatched 'click', or OPC activates twice and re-prices twice. Same rule
   * as backside-finish-handler.js resetSelection().
   */
  function pressEntry(entry, holder) {
    ['mousedown', 'mouseup'].forEach(function (type) {
      entry.dispatchEvent(new MouseEvent(type, { view: window, bubbles: true, cancelable: true }));
    });
    entry.click();
    var input = holder.querySelector(DROPDOWN_VALUE);
    if (input) fireValueEvents(input);
  }

  /**
   * Clear a stale pick by putting Finish back to its placeholder, which also
   * clears the Finish Option beneath it (spike A-005).
   *
   * Without a placeholder (FA07), clear the Finish Option directly: uncheck
   * the swatch, or press the Primer dropdown's own placeholder. A stale Clear
   * Coat or Custom Color has no Finish Option, so nothing more can be done
   * there than report it.
   */
  function resetSelection(step, subject) {
    var top = step.querySelector(TOP_HOLDER);
    if (!top) return;
    var placeholder = placeholderIn(top);
    if (placeholder) {
      pressEntry(placeholder, top);
      return;
    }

    warnOnce('FA07', subject, 'Finish placeholder not found; clearing the stale Finish Option directly.');
    var kind = topLevelEntry(currentSelection(step).finish);
    var holder = holderFor(step, kind);
    if (!holder) return;
    if (kind === 'primer') {
      var primerPlaceholder = placeholderIn(holder);
      if (primerPlaceholder) pressEntry(primerPlaceholder, holder);
      return;
    }
    var checked = holder.querySelector(SWATCH_INPUT + ':checked');
    if (checked) {
      checked.checked = false;
      fireValueEvents(checked);
    }
  }

  /**
   * A reset guard for the step `findStep` returns. `subject` names the context
   * and variant (e.g. "modal:9:301"), so FA05/FA07 report once per variant
   * rather than once per page.
   */
  function guardFor(findStep, subject) {
    return makeResetGuard({
      read: function () {
        var step = findStep();
        return step ? currentSelection(step) : null;
      },
      reset: function () {
        var step = findStep();
        if (step) resetSelection(step, subject());
      },
      onExhausted: function () {
        warnOnce('FA05', subject(), 'OPC kept restoring a Finish this variant cannot be made in after ' + MAX_RESET_ATTEMPTS + ' resets; left as is.');
      }
    });
  }

  function reportUnknownKeys(keys) {
    unknownKeys(keys || []).forEach(function (key) {
      warnOnce('FA02', key, 'payload key "' + key + '" is not in the label table; it governs nothing.');
    });
  }

  /**
   * Filter one step and clear a stale pick in it — or, with no verdict (an
   * unauthored variant), undo this file's filter there.
   */
  function applyTo(step, verdict, guard) {
    if (!verdict) {
      clearFilter(step);
      return;
    }
    applyVerdict(step, verdict);
    guard.applyIfStale(verdict);
  }

  // ---- Product page ----

  /** The product page's step: the one Finish dropdown not inside the modal. */
  function productStep() {
    var holders = document.querySelectorAll(TOP_HOLDER);
    for (var i = 0; i < holders.length; i++) {
      if (!holders[i].closest(MODAL_ROOT)) return stepOf(holders[i]);
    }
    return null;
  }

  var payload = productScript ? parsePayload(productScript) : null;
  var $productForm = payload && typeof jQuery !== 'undefined' ? jQuery('.product-form') : null;

  if ($productForm && $productForm.length) {
    var lastVerdict = null;
    var lastVariantId = null;
    var observer = null;
    var observedRoot = null;
    var productGuard = guardFor(productStep, function () {
      return 'product:' + lastVariantId;
    });

    // OPC rebuilds its menus on re-render, dropping the display:none written
    // here. Re-apply the last verdict rather than re-deriving it: the variant
    // has not changed. The stale check re-runs too: OPC can finish a
    // re-render after the variant change was handled, restoring a pick that
    // is now stale.
    var reapply = debounce(function () {
      if (observedRoot && lastVerdict) applyTo(observedRoot, lastVerdict, productGuard);
    }, REAPPLY_DEBOUNCE_MS);

    /**
     * Watch the step that holds all four finish holders, not any one menu: OPC
     * can replace a menu node outright, which would strand an observer attached
     * to it. childList/subtree only — this file's own writes are attributes
     * (style, data-fa-hidden), so they never re-enter the observer.
     */
    var observe = function (step) {
      if (typeof MutationObserver === 'undefined') return;
      if (step === observedRoot) return;
      if (observer) observer.disconnect();
      observedRoot = step;
      observer = new MutationObserver(reapply);
      observer.observe(step, { childList: true, subtree: true });
    };

    var update = function (variantId) {
      var step = productStep();
      if (!step) return false;

      // The theme's picker fires variantChanged AND writes [name="id"]; handle
      // one change once.
      var id = variantId === null || variantId === undefined ? '' : String(variantId);
      if (id === lastVariantId) return true;
      lastVariantId = id;

      var keys = resolveKeys(payload, variantId);
      reportUnknownKeys(keys);
      lastVerdict = plan(keys);
      productGuard.reset();
      applyTo(step, lastVerdict, productGuard);
      if (lastVerdict) {
        observe(step);
      } else {
        if (observer) observer.disconnect();
        observedRoot = null;
      }
      return true;
    };

    // Two listeners, because two things drive the variant: the theme's picker
    // fires variantChanged on .product-form, and OPC writes [name="id"]
    // directly, firing only a plain change on it. Delegated, so it survives OPC
    // re-rendering the input.
    $productForm.on('variantChanged', function (evt, variant) {
      if (variant && variant.id) update(variant.id);
    });
    $productForm.on('change', '[name="id"]', function () {
      update(jQuery(this).val());
    });

    // OPC mounts the dropdown after DOMContentLoaded. Wait for it on the body —
    // the one observer that cannot be scoped, since its target is what we are
    // waiting for — and disconnect after BOOT_TIMEOUT_MS so a slow, blocked or
    // absent OPC does not leave it alive for the page's lifetime. Not debounced:
    // on a page that changes faster than the debounce window (a slideshow, OPC's
    // price ticks) the pass would never run, and until the dropdown exists a
    // pass is a single querySelector.
    // On a select_first_variant:false template the initial id is empty, which
    // resolves to no verdict: nothing is filtered until the customer picks.
    var tryInitialPass = function () {
      return update($productForm.find('[name="id"]').val());
    };

    if (!tryInitialPass() && typeof MutationObserver !== 'undefined') {
      var boot = new MutationObserver(function () {
        if (tryInitialPass()) boot.disconnect();
      });
      boot.observe(document.body, { childList: true, subtree: true });
      setTimeout(function () {
        if (!productStep()) console.info(LOG_PREFIX + ' FA04 Finish dropdown never mounted; nothing to filter.');
        boot.disconnect();
      }, BOOT_TIMEOUT_MS);
    }
  }

  // ---- Cart "Edit Options" modal ----
  //
  // OPC's own modal, opened against whichever line item's Edit Options was
  // clicked. It carries no variant id, so the line item says which product and
  // variant it opened for, and the modal's own option selects say which
  // variant it has been switched to since.
  //
  // Wired on every page this file loads on, so a wood product page whose cart
  // drawer gains a wood line after load (its payload arrives with the drawer's
  // re-render) is covered. A page that did not load this file at all — no wood
  // product, no wood line in the cart — is not: the theme re-renders the drawer
  // through innerHTML, which never runs the snippet's <script>. Same limit as
  // backside-finish-handler.js; the store currently uses the cart page.

  function modalStep() {
    return stepOf(document.querySelector(MODAL_ROOT + ' ' + TOP_HOLDER));
  }

  function readCartData() {
    var scripts = document.querySelectorAll(CART_DATA);
    var parsed = [];
    for (var i = 0; i < scripts.length; i++) parsed.push(parsePayload(scripts[i]));
    return mergeCartPayloads(parsed);
  }

  function selectedOptionsInModal() {
    var selected = {};
    var selects = document.querySelectorAll(MODAL_ROOT + ' select[id^="' + MODAL_SELECT_PREFIX + '"]');
    for (var i = 0; i < selects.length; i++) {
      var name = selects[i].name || selects[i].id.slice(MODAL_SELECT_PREFIX.length);
      selected[name] = selects[i].value;
    }
    return selected;
  }

  var pending = null;
  // "productId:variantId" the modal was last synced for. Kept here, not on the
  // modal's DOM: OPC can replace the modal's nodes while restoring a pick, and
  // a stamp on the node would re-arm the reset budget on every replacement.
  var modalStamp = null;
  var modalObserver = null;
  // Its own budget: a product page can be open behind a cart drawer's modal,
  // and the two contexts should not spend each other's attempts.
  var modalGuard = guardFor(modalStep, function () {
    return 'modal:' + modalStamp;
  });

  /**
   * Bring the open modal in line with its variant's verdict.
   *
   * Runs on every mutation while the modal is open, not once per render: the
   * modal mounts with Finish at its placeholder and OPC restores the line's
   * saved Finish 4–7 s later (D-027), so a stale pick appears long after the
   * first render. Re-applying is cheap — setVisible writes nothing where the
   * state already holds — and never re-enters: this file's writes are
   * attributes and the observer watches childList only.
   */
  function syncModal() {
    if (!pending) return;
    var step = modalStep();
    if (!step) return;
    var entry = readCartData()[pending.productId];
    if (!entry) return;

    var selected = selectedOptionsInModal();
    var variant = modalVariant(entry, selected, pending.variantId);
    // Selects that have mounted but name none of the product's options: OPC
    // renamed them, so the modal cannot follow a Wood Type switch.
    if (variant.fromLine && Object.keys(selected).length) {
      warnOnce('FA06', pending.productId, 'modal selects match none of the product\'s options; using the line item\'s variant.', 'info');
    }

    // A new line, or the modal switched to a new variant: a fresh reset budget.
    var stamp = pending.productId + ':' + variant.id;
    if (stamp !== modalStamp) {
      modalStamp = stamp;
      modalGuard.reset();
    }

    var keys = resolveKeys(entry, variant.id);
    reportUnknownKeys(keys);
    applyTo(step, plan(keys), modalGuard);
  }

  // Capture phase, to record the line before OPC's own click handler opens the
  // modal for it.
  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;
    var item = target.closest(LINE_ITEM);
    if (!item) return;
    pending = {
      productId: item.getAttribute('data-fa-product'),
      variantId: item.getAttribute('data-fa-variant')
    };
    modalStamp = null;

    // Started on the first line-item click rather than at load, so a page
    // whose cart is never edited carries no body observer at all. It then
    // stays for the page's life: the modal opens and closes repeatedly.
    if (!modalObserver && typeof MutationObserver !== 'undefined') {
      modalObserver = new MutationObserver(syncModal);
      modalObserver.observe(document.body, { childList: true, subtree: true });
    }
  }, true);

  // Switching Wood Type inside the modal changes the variant. OPC re-renders
  // after it, which the observer catches, but syncing here as well means the
  // filter follows the select even when OPC's re-render is slow.
  document.addEventListener('change', function (evt) {
    var target = evt.target;
    if (!pending || !target || target.tagName !== 'SELECT') return;
    if ((target.id || '').indexOf(MODAL_SELECT_PREFIX) !== 0 || !target.closest(MODAL_ROOT)) return;
    syncModal();
  });
})();
